import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Checkbox, Collapse, Empty, Space, Spin, Tag, Typography } from 'antd';
import { fetchPermissionSets, fetchUserAccess, updateUserPermissionSets } from './AccessControlService';

const { Title, Text } = Typography;

function normalisePermissionSetId(item) {
  return item?.permissionSet || item?.permissionSetId || item?.id || '';
}

function normalisePermissionSetName(item) {
  return item?.name || item?.displayName || normalisePermissionSetId(item);
}

function normalisePermissionSetDescription(item) {
  return item?.description || item?.summary || '';
}

function normalisePermissionRows(item) {
  const permissions = Array.isArray(item?.permissions) ? item.permissions : [];
  return permissions.map((permission) => ({
    id: permission?.permission || permission?.permissionId || permission?.id || '',
    name: permission?.name || permission?.label || permission?.permission || permission?.permissionId || permission?.id || '',
  })).filter((permission) => permission.id || permission.name);
}

function normalisePermissionName(value) {
  if (!value) return '';
  return value
    .toString()
    .toLowerCase()
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function groupPermissionIds(permissionIds) {
  const grouped = {};

  (Array.isArray(permissionIds) ? permissionIds : []).forEach((permissionId) => {
    if (!permissionId) return;

    const raw = permissionId.toString();
    const parts = raw.split('_');
    const sectionKey = parts.length > 1 ? parts[0] : 'GENERAL';

    if (!grouped[sectionKey]) {
      grouped[sectionKey] = [];
    }

    grouped[sectionKey].push(raw);
  });

  return Object.entries(grouped).map(([section, ids]) => ({
    section,
    permissions: ids,
  }));
}

export default function AccessManagementPanel({ token, employee, employeeId }) {
  const resolvedEmployeeId = employeeId || employee?.id || employee?.userId || employee?.employeeId;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [permissionSets, setPermissionSets] = useState([]);
  const [roleId, setRoleId] = useState(employee?.role || employee?.roleId || '—');
  const [selectedPermissionSetIds, setSelectedPermissionSetIds] = useState([]);
  const [effectivePermissionIds, setEffectivePermissionIds] = useState([]);

  const permissionSetCards = useMemo(() => {
    return permissionSets.map((item) => {
      const id = normalisePermissionSetId(item);
      return {
        id,
        name: normalisePermissionSetName(item),
        description: normalisePermissionSetDescription(item),
        permissions: normalisePermissionRows(item),
      };
    }).filter((item) => item.id);
  }, [permissionSets]);

  const effectivePermissions = useMemo(() => groupPermissionIds(effectivePermissionIds), [effectivePermissionIds]);

  const loadData = async () => {
    if (!token || !resolvedEmployeeId) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const [permissionSetResponse, userAccessResponse] = await Promise.all([
        fetchPermissionSets(token),
        fetchUserAccess(token, resolvedEmployeeId),
      ]);

      setPermissionSets(permissionSetResponse);
      setRoleId(userAccessResponse?.roleId || employee?.role || employee?.roleId || '—');
      setSelectedPermissionSetIds(Array.isArray(userAccessResponse?.permissionSetIds) ? userAccessResponse.permissionSetIds : []);
      setEffectivePermissionIds(Array.isArray(userAccessResponse?.permissionIds) ? userAccessResponse.permissionIds : []);
    } catch (loadError) {
      console.error(loadError);
      setError('Unable to load employee access details.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [token, resolvedEmployeeId]);

  const togglePermissionSet = (permissionSetId, checked) => {
    setSuccessMessage('');

    setSelectedPermissionSetIds((previous) => {
      if (checked) {
        if (previous.includes(permissionSetId)) return previous;
        return [...previous, permissionSetId];
      }

      return previous.filter((id) => id !== permissionSetId);
    });
  };

  const handleSave = async () => {
    if (!token || !resolvedEmployeeId) {
      setError('Employee information is missing.');
      return;
    }

    setSaving(true);
    setError('');
    setSuccessMessage('');

    try {
      await updateUserPermissionSets(token, resolvedEmployeeId, selectedPermissionSetIds);
      const refreshed = await fetchUserAccess(token, resolvedEmployeeId);
      setRoleId(refreshed?.roleId || roleId || '—');
      setSelectedPermissionSetIds(Array.isArray(refreshed?.permissionSetIds) ? refreshed.permissionSetIds : selectedPermissionSetIds);
      setEffectivePermissionIds(Array.isArray(refreshed?.permissionIds) ? refreshed.permissionIds : []);
      setSuccessMessage('Access updated successfully.');
    } catch (saveError) {
      console.error(saveError);
      setError('Unable to save access updates.');
    } finally {
      setSaving(false);
    }
  };

  if (!resolvedEmployeeId) {
    return <Empty description="No employee selected" />;
  }

  if (loading) {
    return (
      <Card>
        <Spin />
      </Card>
    );
  }

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      {error ? <Alert type="error" showIcon message={error} /> : null}
      {successMessage ? <Alert type="success" showIcon message={successMessage} /> : null}

      <Card title="Role" size="small">
        <Tag color="blue">{roleId || '—'}</Tag>
      </Card>

      <Card
        title="Permission Sets"
        size="small"
        extra={(
          <Button type="primary" onClick={handleSave} loading={saving}>
            Save Access
          </Button>
        )}
      >
        {permissionSetCards.length === 0 ? (
          <Empty description="No permission sets available" />
        ) : (
          <Collapse
            items={permissionSetCards.map((item) => ({
              key: item.id,
              label: (
                <Space direction="vertical" size={2} style={{ width: '100%' }}>
                  <Space>
                    <Checkbox
                      checked={selectedPermissionSetIds.includes(item.id)}
                      onClick={(event) => event.stopPropagation()}
                      onChange={(event) => togglePermissionSet(item.id, event.target.checked)}
                    >
                      <Text strong>{item.name}</Text>
                    </Checkbox>
                  </Space>
                  {item.description ? <Text type="secondary">{item.description}</Text> : null}
                </Space>
              ),
              children: (
                <Space direction="vertical" size={6} style={{ width: '100%' }}>
                  <Text strong>Included Permissions</Text>
                  {item.permissions.length ? item.permissions.map((permission) => (
                    <Text key={permission.id || permission.name}>✓ {permission.name || permission.id}</Text>
                  )) : <Text type="secondary">No permission details provided</Text>}
                </Space>
              ),
            }))}
          />
        )}
      </Card>

      <Card title="Effective Permissions" size="small">
        {!effectivePermissions.length ? (
          <Empty description="No effective permissions" />
        ) : (
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            {effectivePermissions.map((section) => (
              <div key={section.section}>
                <Title level={5} style={{ marginBottom: 8 }}>{normalisePermissionName(section.section) || section.section}</Title>
                <Space direction="vertical" size={4}>
                  {section.permissions.map((permission) => (
                    <Text key={permission}>✓ {normalisePermissionName(permission) || permission}</Text>
                  ))}
                </Space>
              </div>
            ))}
          </Space>
        )}
      </Card>
    </Space>
  );
}
